// Shared "locked credential" UX for admin secret-vault screens (Marketplace Apps,
// Payment Gateways, Carrier Accounts, Bank/Wise).
//
// Volkan direction 2026-07-19: an important connection that is already configured
// should read as clearly CONNECTED (green tick), keep its already-entered secret
// fields LOCKED (greyed) rather than showing empty inputs, and only become
// editable after an explicit admin confirmation — so a saved secret is never
// silently overwritten.
//
// Usage per screen:
//   const gate = useEditGate(configured)
//   {gate.editing
//     ? <TheForm onCancel={configured ? gate.lock : undefined} onSaved={gate.lock} />
//     : <LockedCredentialNotice hint={secretHint} onEdit={gate.requestEdit} />}

import { useState } from 'react'
import { PexIcon } from './icons/PexIcon'
import { useT } from '../i18n'
import { sharedUiI18n } from '../i18n/components/shared-ui'

// ── Configured badge ──────────────────────────────────────────────────────

export function ConfiguredBadge({
  configured,
  sourceLabel,
}: {
  configured: boolean
  // Optional sub-label, e.g. "server config" when the credential lives in env.
  sourceLabel?: string | null
}) {
  const t = useT(sharedUiI18n).credentialLock
  if (!configured) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]">
        {t.notConfigured}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-[color-mix(in_srgb,var(--color-success)_12%,var(--color-surface))] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-success-foreground)] ring-1 ring-[color-mix(in_srgb,var(--color-success)_38%,var(--color-border))]">
      <PexIcon name="success" className="h-[11px] w-[11px]" size="xs" tone="inherit" />
      {t.connected}
      {sourceLabel && <span className="font-normal opacity-80">· {sourceLabel}</span>}
    </span>
  )
}

// ── Edit gate ─────────────────────────────────────────────────────────────
// useEditGate + EditGate live in credential-lock.lib.ts.

// ── Locked notice (replaces the form while a credential is saved) ──────────

export function LockedCredentialNotice({
  hint,
  onEdit,
  label,
}: {
  // Non-secret last-4 hint (e.g. "••••4242"). Optional.
  hint?: string | null
  // Called after the admin confirms the replace intent.
  onEdit: () => void
  // Noun for the message, e.g. "Wise credentials" / "Shopify credentials".
  label?: string
}) {
  const t = useT(sharedUiI18n).credentialLock
  const [confirming, setConfirming] = useState(false)

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2.5 text-sm text-[var(--color-text)]">
        <PexIcon name="verified" className="text-[var(--color-success-foreground)]" size="xs" tone="inherit" />
        <span>
          {t.savedPrefix(label ?? t.defaultLabel)}{hint ? <span className="font-mono text-[var(--color-text-muted)]"> ({hint})</span> : ''}{' '}
          {t.savedSuffix}
        </span>
      </div>

      {confirming ? (
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-[var(--color-text-muted)]">{t.replaceConfirm}</span>
          <button
            type="button"
            onClick={() => {
              setConfirming(false)
              onEdit()
            }}
            className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-rose-700"
          >
            {t.yesReplace}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface)]"
          >
            {t.cancel}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="inline-flex shrink-0 items-center gap-1.5 self-end rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface)] sm:self-auto"
        >
          <PexIcon name="lock" className="h-[12px] w-[12px]" size="xs" tone="inherit" />
          {t.editCredentials}
        </button>
      )}
    </div>
  )
}
